import { ChangeEvent, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import type { AdminFileBucket } from "@equipped/core/backend/client";
import { useAuth } from "../auth/AuthContext";
import { MainCard } from "../components/MainCard";
import { prefixSegments, prefixUpTo, stripPrefix } from "../lib/manifestFile";

const DIRECT_LIMIT = 25 * 1024 * 1024;

function sizeLabel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KiB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MiB`;
}

interface Transfer {
  name: string;
  progress: number;
  error?: string;
}

/** docs/350 v0 File Manager: the manifest picker's R2 browser promoted to a
 * bucket-aware browse/upload/delete page. Large files are sliced in the browser;
 * each completed part advances progress and can be retried without restarting
 * already-recorded parts in a future resume UI. */
export function FilesPage() {
  const { client, token } = useAuth();
  const [bucket, setBucket] = useState<AdminFileBucket>("gated");
  const [prefix, setPrefix] = useState("");
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const listing = useQuery({
    queryKey: ["admin-content-listing", bucket, prefix],
    queryFn: () => client!.adminListContent(token!, prefix, bucket),
    enabled: !!client && !!token,
  });

  const updateTransfer = (name: string, patch: Partial<Transfer>) =>
    setTransfers((rows) => rows.map((row) => row.name === name ? { ...row, ...patch } : row));

  async function upload(file: File) {
    const key = `${prefix}${file.name}`;
    setTransfers((rows) => [...rows.filter((row) => row.name !== file.name), { name: file.name, progress: 0 }]);
    try {
      if (file.size <= DIRECT_LIMIT) {
        await client!.adminPutFile(token!, key, file, bucket);
        updateTransfer(file.name, { progress: 100 });
      } else {
        const session = await client!.adminCreateMultipart(token!, {
          key,
          bucket,
          size: file.size,
          contentType: file.type || "application/octet-stream",
          filename: file.name,
        });
        for (let partNumber = 1; partNumber <= session.partCount; partNumber += 1) {
          const start = (partNumber - 1) * session.partSize;
          await client!.adminPutMultipartPart(token!, session.uploadId, partNumber, file.slice(start, Math.min(start + session.partSize, file.size)));
          updateTransfer(file.name, { progress: Math.round((partNumber / session.partCount) * 100) });
        }
        await client!.adminCompleteMultipart(token!, session.uploadId);
      }
      await listing.refetch();
    } catch (cause) {
      updateTransfer(file.name, { error: (cause as Error).message });
    }
  }

  function onFiles(event: ChangeEvent<HTMLInputElement>) {
    const files = [...(event.target.files ?? [])];
    event.target.value = "";
    void (async () => {
      for (const file of files) await upload(file);
    })();
  }

  async function remove(key: string) {
    if (!window.confirm(`Delete ${key}? This cannot be undone.`)) return;
    setError(null);
    try {
      await client!.adminDeleteFile(token!, key, bucket);
      await listing.refetch();
    } catch (cause) {
      setError((cause as Error).message);
    }
  }

  const segs = prefixSegments(prefix);
  return (
    <MainCard
      title="File Manager"
      subheader="Browse, upload, and delete content objects. Files over 25 MiB use resumable multipart upload."
      secondary={<Button variant="contained" onClick={() => input.current?.click()}>Upload files</Button>}
    >
      <input ref={input} hidden multiple type="file" onChange={onFiles} />
      <Tabs value={bucket} onChange={(_, value: AdminFileBucket) => { setBucket(value); setPrefix(""); }} sx={{ mb: 2 }}>
        <Tab value="gated" label="Gated" />
        <Tab value="public" label="Public" />
      </Tabs>
      <Alert severity={bucket === "gated" ? "info" : "warning"} sx={{ mb: 2 }}>
        {bucket === "gated" ? "Gated files require a valid content token." : "Public files are freely accessible and may be cached."}
      </Alert>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, mb: 2 }}>
        <Button size="small" onClick={() => setPrefix("")}>root</Button>
        {segs.map((segment, index) => (
          <Box key={`${segment}-${index}`} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography color="text.secondary">›</Typography>
            <Button size="small" onClick={() => setPrefix(prefixUpTo(prefix, index))}>{segment}</Button>
          </Box>
        ))}
      </Box>
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {listing.isError ? <Alert severity="error">{(listing.error as Error).message}</Alert> : null}
      {listing.isLoading ? <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress /></Box> : (
        <Stack spacing={0.5}>
          {(listing.data?.folders ?? []).map((folder) => (
            <Button key={folder} color="inherit" sx={{ justifyContent: "flex-start" }} onClick={() => setPrefix(folder)}>
              📁 {stripPrefix(folder, prefix).replace(/\/$/, "")}
            </Button>
          ))}
          {(listing.data?.files ?? []).map((file) => (
            <Box key={file.key} sx={{ display: "flex", alignItems: "center", gap: 2, py: 1, borderBottom: 1, borderColor: "divider" }}>
              <Typography sx={{ flex: 1 }}>{stripPrefix(file.key, prefix)}</Typography>
              <Typography variant="body2" color="text.secondary">{sizeLabel(file.size)}</Typography>
              <Typography variant="body2" color="text.secondary">{file.uploaded ? new Date(file.uploaded).toLocaleString() : ""}</Typography>
              <Button size="small" color="error" onClick={() => void remove(file.key)}>Delete</Button>
            </Box>
          ))}
          {!listing.isLoading && (listing.data?.folders.length ?? 0) === 0 && (listing.data?.files.length ?? 0) === 0 ? (
            <Typography color="text.secondary">Nothing here. R2 folders appear when they contain at least one file.</Typography>
          ) : null}
        </Stack>
      )}
      {transfers.length > 0 ? (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Uploads</Typography>
          <Stack spacing={1}>
            {transfers.map((transfer) => (
              <Box key={transfer.name}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2">{transfer.name}</Typography>
                  <Typography variant="body2" color={transfer.error ? "error" : "text.secondary"}>{transfer.error ?? `${transfer.progress}%`}</Typography>
                </Box>
                <LinearProgress variant="determinate" value={transfer.progress} color={transfer.error ? "error" : "primary"} />
              </Box>
            ))}
          </Stack>
        </Box>
      ) : null}
    </MainCard>
  );
}
